/* Carbon Co. — theme behavior (Phase 4 + motion pack)
   Progressive enhancement: everything works without JS; GSAP/ScrollTrigger/Lenis enrich when present.
   Desktop-rich, mobile-light, everything off under prefers-reduced-motion.
   GPU-only transforms/opacity; reveals run once; scroll-driven pieces use ScrollTrigger. */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var desktop = window.matchMedia('(min-width: 990px)').matches;
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var hasGsap = !!(window.gsap && window.ScrollTrigger);
  document.body.classList.add('cbn-js');
  if (hasGsap && !reduced) {
    gsap.registerPlugin(ScrollTrigger);
    if (desktop) document.body.classList.add('cbn-gsap'); /* enables the CSS mask-wipe reveals */
  }

  /* ---------- theme toggle (persists; default dark) ---------- */
  function initToggle() {
    var root = document.documentElement;
    document.querySelectorAll('.cbn-toggle').forEach(function (tgl) {
      function sync() {
        tgl.setAttribute('aria-pressed', root.getAttribute('data-carbon-theme') === 'light' ? 'true' : 'false');
      }
      sync();
      tgl.addEventListener('click', function () {
        var light = root.getAttribute('data-carbon-theme') === 'light';
        if (light) root.removeAttribute('data-carbon-theme');
        else root.setAttribute('data-carbon-theme', 'light');
        try {
          localStorage.setItem('carbon-theme', light ? 'dark' : 'light');
        } catch (e) {}
        sync();
      });
    });
  }

  /* ---------- announcement bar: continuous RTL marquee (static under reduced-motion) ----------
     The bar must be FULL from first paint and never show an empty stretch: each half-track is
     over-filled to cover the widest of (viewport, bar) with generous headroom, and the whole
     thing is rebuilt on resize / after fonts settle so measurements never go stale. */
  function initMarquee() {
    if (reduced) return;
    var msg = document.querySelector('.announcement-bar__message');
    if (!msg) return;
    var src = msg.querySelector('span');
    if (!src || !src.textContent.trim()) return;

    function build() {
      var old = msg.querySelector('.cbn-marquee');
      if (old) old.remove();
      var track = document.createElement('span');
      track.className = 'cbn-marquee';
      track.setAttribute('aria-hidden', 'true');
      track.style.display = 'flex';
      function makeUnit() {
        var u = document.createElement('span');
        u.className = 'cbn-marquee__u';
        u.innerHTML = src.innerHTML;
        return u;
      }
      var grp = document.createElement('span');
      grp.className = 'cbn-marquee__grp';
      grp.appendChild(makeUnit());
      msg.classList.add('cbn-mq-on');
      msg.appendChild(track);
      track.appendChild(grp);
      var unitW = grp.firstChild.offsetWidth;
      if (!unitW) {
        /* not measurable yet — leave the static text, try again on load */
        track.remove();
        msg.classList.remove('cbn-mq-on');
        return false;
      }
      var need = Math.max(window.innerWidth, msg.clientWidth || 0) * 1.5 + unitW;
      var copies = Math.max(3, Math.ceil(need / unitW));
      for (var i = 1; i < copies; i++) grp.appendChild(makeUnit());
      track.appendChild(grp.cloneNode(true));
      var groupW = grp.offsetWidth || unitW * copies;
      track.style.setProperty('--cbn-mq-dur', Math.max(24, Math.round(groupW / 42)) + 's');
      /* keep the original text for screen readers, hide it visually */
      src.style.position = 'absolute';
      src.style.width = '1px';
      src.style.height = '1px';
      src.style.overflow = 'hidden';
      src.style.clipPath = 'inset(50%)';
      return true;
    }

    var ok = build();
    window.addEventListener('load', function () {
      build();
    }, { once: true });
    var rT;
    window.addEventListener('resize', function () {
      clearTimeout(rT);
      rT = setTimeout(build, 250);
    });
  }

  /* ---------- sale badges: tag them so CSS can style sale vs sold-out differently ---------- */
  function initBadges() {
    document.querySelectorAll('.badge').forEach(function (b) {
      var t = (b.textContent || '').trim();
      if (/خصم|تخفيض|وفّ?ر|sale/i.test(t)) b.classList.add('cbn-badge-sale');
    });
  }

  /* ---------- PDP gallery counter: show the REAL number of rendered images ---------- */
  function initPdpCounter() {
    document.querySelectorAll('.product__media-wrapper').forEach(function (w) {
      var items = w.querySelectorAll('.product__media-item');
      if (!items.length) return;
      w.querySelectorAll('.slider-counter--total').forEach(function (t) {
        t.textContent = items.length;
      });
    });
  }

  /* ---------- hero: intro headline reveal — simple fade+rise (ash assembly removed on owner request).
     Section H2 heads use the generic fail-safe .cbn-rv reveal. ---------- */
  function initIntroReveal() {
    /* anchor on the headline itself — the session intro LOADER also carries .cbn-intro but has no h1 */
    var h1 = document.querySelector('.cbn-intro h1');
    var intro = h1 && h1.closest('.cbn-intro');
    if (!intro) return;
    if (reduced || !window.gsap) return;
    var eyebrow = intro.querySelector('.cbn-eyebrow');
    var sub = intro.querySelector('.cbn-sub');
    var ctas = intro.querySelector('.cbn-cta-row');
    [eyebrow, h1, sub, ctas].forEach(function (el) {
      if (el) el.classList.remove('cbn-rv');
    });
    /* triggered on view (the intro sits below the hero media, often below the fold), once.
       FAIL-SAFE: from-tweens with immediateRender:false — nothing hidden until the play starts. */
    var tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      scrollTrigger: window.ScrollTrigger ? { trigger: intro, start: 'top 92%', once: true } : undefined,
    });
    if (eyebrow) tl.from(eyebrow, { opacity: 0, y: 12, duration: 0.5, immediateRender: false }, 0.1);
    tl.from(h1, { opacity: 0, y: 18, duration: 0.7, immediateRender: false }, 0.24);
    if (sub) tl.from(sub, { opacity: 0, y: 16, duration: 0.55, immediateRender: false }, 0.5);
    if (ctas) tl.from(ctas, { opacity: 0, y: 18, duration: 0.55, immediateRender: false }, 0.68);
  }

  /* ---------- scroll reveals — FAIL-SAFE ----------
     Content is VISIBLE by default (nothing is pre-hidden in CSS or at boot). When an
     element first enters the viewport it gets .cbn-anim, whose CSS keyframes play the
     fade+rise once and always END at the natural, visible state. If no observer ever
     fires (or JS dies mid-page), everything simply stays visible. */
  function initReveals() {
    var els = document.querySelectorAll('.cbn-rv');
    if (!els.length || reduced) return;
    /* per-section stagger index */
    var counters = new Map();
    els.forEach(function (el) {
      var sec = el.closest('.shopify-section') || document.body;
      var n = counters.get(sec) || 0;
      counters.set(sec, n + 1);
      el.dataset.cbnIdx = n;
    });
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var el = e.target;
          io.unobserve(el);
          el.style.animationDelay = Math.min(parseInt(el.dataset.cbnIdx, 10) % 6, 5) * 0.06 + 's';
          el.classList.add('cbn-anim', 'cbn-in');
        });
      },
      { threshold: 0.05 }
    );
    els.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------- Lenis smooth scroll (desktop only, gentle) ---------- */
  function initLenis() {
    if (reduced || !desktop || !window.Lenis) return;
    var lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    function raf(t) {
      lenis.raf(t);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    if (window.ScrollTrigger) {
      lenis.on('scroll', ScrollTrigger.update);
    }
  }

  /* ---------- smoke dividers: pause off-screen ---------- */
  function initDividers() {
    var divs = document.querySelectorAll('.cbn-smoke-div');
    if (!divs.length) return;
    var dio = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          e.target.classList.toggle('cbn-paused', !e.isIntersecting);
        });
      },
      { rootMargin: '80px' }
    );
    divs.forEach(function (el) {
      el.classList.add('cbn-paused');
      dio.observe(el);
    });
  }

  /* ---------- rails: manual swipe/drag side-scroll on every device (no scroll pinning) ---------- */
  function attachRailDrag(rail) {
    {
      var down = false,
        startX = 0,
        startScroll = 0,
        moved = false;
      rail.addEventListener('pointerdown', function (e) {
        down = true;
        moved = false;
        startX = e.clientX;
        startScroll = rail.scrollLeft;
        rail.classList.add('cbn-dragging');
        rail.setPointerCapture(e.pointerId);
      });
      rail.addEventListener('pointermove', function (e) {
        if (!down) return;
        var d = e.clientX - startX;
        if (Math.abs(d) > 4) moved = true;
        rail.scrollLeft = startScroll - d;
      });
      ['pointerup', 'pointercancel'].forEach(function (ev) {
        rail.addEventListener(ev, function () {
          down = false;
          rail.classList.remove('cbn-dragging');
        });
      });
      rail.addEventListener(
        'click',
        function (e) {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
      var hint = rail.closest('.shopify-section, section, div')
        ? rail.closest('.shopify-section, section, div').querySelector('.cbn-rail-hint')
        : null;
      rail.addEventListener(
        'scroll',
        function () {
          if (hint) hint.classList.add('cbn-off');
        },
        { once: true }
      );
    }
  }
  function initRails() {
    document.querySelectorAll('.cbn-rail').forEach(attachRailDrag);
  }

  /* ---------- auto-scrolling review carousels ----------
     Continuous marquee: the card set is duplicated once (clones aria-hidden) and the rail
     advances via rAF; when it has moved one full set width it wraps seamlessly. Pauses on
     hover / press / focus / wheel / touch and while off-screen; scroll-snap is disabled in
     auto mode; RTL advances right-to-left. OFF under reduced-motion. */
  function setupAutoRail(rail) {
    if (!rail || reduced || rail.dataset.cbnAuto) return;
    if (rail.scrollWidth <= rail.clientWidth + 40) return; /* everything already fits */
    rail.dataset.cbnAuto = '1';
    rail.classList.add('cbn-auto');
    var setCount = rail.children.length;
    Array.prototype.slice.call(rail.children).forEach(function (k) {
      var c = k.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.querySelectorAll('a, button, [tabindex]').forEach(function (f) {
        f.setAttribute('tabindex', '-1');
      });
      rail.appendChild(c);
    });
    var rtl = document.documentElement.dir === 'rtl';
    var span = 0;
    function measure() {
      span = Math.abs(rail.children[setCount].offsetLeft - rail.children[0].offsetLeft);
    }
    measure();
    var mT;
    window.addEventListener('resize', function () {
      clearTimeout(mT);
      mT = setTimeout(measure, 180);
    });
    var holdUntil = 0,
      hovering = false,
      visible = false;
    function hold(ms) {
      holdUntil = Math.max(holdUntil, performance.now() + ms);
    }
    rail.addEventListener('pointerenter', function () {
      hovering = true;
    });
    rail.addEventListener('pointerleave', function () {
      hovering = false;
      hold(500);
    });
    rail.addEventListener('pointerdown', function () {
      hold(3500);
    });
    rail.addEventListener('wheel', function () {
      hold(3500);
    }, { passive: true });
    rail.addEventListener('touchstart', function () {
      hold(4000);
    }, { passive: true });
    rail.addEventListener('focusin', function () {
      hovering = true;
    });
    rail.addEventListener('focusout', function () {
      hovering = false;
      hold(500);
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        visible = e.isIntersecting;
      });
    }, { rootMargin: '60px' });
    io.observe(rail);
    var pos = Math.abs(rail.scrollLeft),
      last = 0;
    function tick(t) {
      if (!rail.isConnected) return; /* rail was replaced (app re-render) — stop this loop */
      requestAnimationFrame(tick);
      if (!span) return;
      var dt = Math.min(0.06, (t - last) / 1000);
      last = t;
      if (!visible || hovering || t < holdUntil || document.hidden) {
        pos = Math.abs(rail.scrollLeft) % span; /* resync so a user scroll is respected on resume */
        return;
      }
      pos = (pos + 26 * dt) % span;
      rail.scrollLeft = rtl ? -pos : pos;
    }
    requestAnimationFrame(tick);
  }
  function initAutoRails() {
    if (reduced) return;
    setupAutoRail(document.querySelector('[data-cbn-revs]')); /* homepage reviews */
    /* PDP: once Judge.me hydrates the review list, restyle it into the same carousel.
       The observer stays alive permanently: Judge.me REPLACES the reviews container when
       its JS hydrates over the server-rendered metafield, wiping our clones/state — every
       rebuild re-arms on the fresh node (dataset guard prevents double-arming). */
    var widget = document.querySelector('.cbn-jdgm--widget');
    if (widget) {
      var armT;
      var arm = function () {
        var wrap = widget.querySelector('.jdgm-rev-widg__reviews');
        if (!wrap || wrap.dataset.cbnAuto || wrap.querySelectorAll('.jdgm-rev').length < 2) return;
        widget.classList.add('cbn-jdgm-railmode');
        wrap.classList.add('cbn-rail');
        attachRailDrag(wrap);
        setupAutoRail(wrap);
      };
      new MutationObserver(function () {
        clearTimeout(armT);
        armT = setTimeout(arm, 500); /* debounce the hydration burst, then (re-)arm */
      }).observe(widget, { childList: true, subtree: true });
      setTimeout(arm, 300); /* server-rendered metafield may already contain the reviews */
    }
  }

  /* (the old pinned sizes reveal is gone — its scrub rewound cards to opacity 0 when
     scrolling back up, which read as "the section vanished". Sizes cards now enter
     through the same fail-safe tilt-in as every other card.) */

  /* ---------- hero: lazy video attach (desktop AND mobile), aspect-fit, whisper zoom ----------
     The media box adopts the video's REAL aspect ratio so the full frame (logo included)
     is always visible — no cover-cropping, and on mobile the video scales down to fit. */
  function initHero() {
    var hero = document.querySelector('.cbn-hero');
    if (!hero) return;
    var media = hero.querySelector('.cbn-hero__media');
    var vid = hero.querySelector('video[data-cbn-video]');
    if (vid && media) {
      vid.addEventListener('loadedmetadata', function () {
        if (vid.videoWidth && vid.videoHeight) {
          media.style.setProperty('--cbn-hero-ratio', vid.videoWidth + ' / ' + vid.videoHeight);
          if (window.ScrollTrigger) ScrollTrigger.refresh();
        }
      });
    }
    if (vid && !reduced && !saveData) {
      /* round 40: phones (<750px, Dawn's breakpoint) get the light 720px encode */
      var small = window.matchMedia('(max-width: 749px)').matches;
      var attach = function () {
        vid.querySelectorAll('source[data-src]').forEach(function (s) {
          s.src = small && s.dataset.srcMobile ? s.dataset.srcMobile : s.dataset.src;
        });
        vid.load();
        vid.addEventListener(
          'playing',
          function () {
            vid.classList.add('cbn-live');
            if (media) media.classList.add('cbn-video-live'); /* poster blur-up under the cross-fade */
          },
          { once: true }
        );
        var p = vid.play();
        if (p && p.catch) p.catch(function () {});
      };
      /* round 40: the video request starts only after window load AND an idle slot, so it
         never competes with the poster (LCP) or the critical CSS/JS */
      var whenIdle = function () {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(attach, { timeout: 2000 });
        } else {
          setTimeout(attach, 300);
        }
      };
      if (document.readyState === 'complete') {
        whenIdle();
      } else {
        window.addEventListener('load', whenIdle, { once: true });
      }
    }
    if (reduced || !window.gsap || !media) return;
    /* whisper-level breathing only (≤0.6% edge loss) — the full frame stays readable.
       round 41: was `gsap.fromTo(media, {scale: 1}, {scale: 1.012, duration: 26, ease: 'sine.inOut',
       yoyo: true, repeat: -1})` — an infinite tween that restyled the hero on the MAIN thread every
       frame. Same start point, same conditions; the motion itself is now the CSS animation
       .cbn-zoom (carbon.css: same range, duration and sine curve), which runs on the compositor. */
    media.classList.add('cbn-zoom');
  }

  /* ---------- sticky announcement bar: expose its height so the sticky header sits below it ---------- */
  function initStickyBar() {
    var bar = document.querySelector('.utility-bar');
    if (!bar) return;
    function set() {
      document.documentElement.style.setProperty('--cbn-ann-h', bar.offsetHeight + 'px');
    }
    set();
    window.addEventListener('resize', set);
  }

  /* ---------- round 33: sticky bottom cart bar = the SAME carbon-shipbar ----------
     The shell hosts a real carbon-shipbar render. On every cart change we rebuild
     the shipbar node from a pristine clone and REPLACE it — the sweep-in animation
     replays and initTierCelebrate's body observer sees a fresh node, so tier
     celebrations fire exactly like the in-cart bar. Also: Dawn's drawer no longer
     auto-opens on add (silent add), the header count bubble updates manually, and
     the bar hides while the drawer is open (no duplicate). */
  function initCartStick() {
    var bar = document.querySelector('[data-cbn-stick]');
    if (!bar) return;
    var host = bar.querySelector('[data-cbn-stick-host]');
    var info = bar.querySelector('[data-cbn-stick-info]');
    var pop = bar.querySelector('[data-cbn-stick-pop]');
    var T1 = parseInt(bar.getAttribute('data-t1'), 10) || 1000;
    var T2 = parseInt(bar.getAttribute('data-t2'), 10) || 1500;
    var proto = host.querySelector('.cbn-shipbar');
    if (!proto) return;
    var template = proto.cloneNode(true);

    function fmt(units) {
      return (units / 100).toFixed(3);
    }
    function buildBar(total) {
      var node = template.cloneNode(true);
      node.classList.remove('cbn-shipbar--t1', 'cbn-shipbar--done');
      if (total >= T2) node.classList.add('cbn-shipbar--done');
      else if (total >= T1) node.classList.add('cbn-shipbar--t1');
      var fill = node.querySelector('.cbn-shipbar__fill');
      if (fill) fill.style.width = Math.min(100, (total * 100) / T2) + '%';
      var ticks = node.querySelectorAll('.cbn-shipbar__tick');
      if (ticks[0]) ticks[0].classList.toggle('cbn-shipbar__tick--hit', total >= T1);
      if (ticks[1]) ticks[1].classList.toggle('cbn-shipbar__tick--hit', total >= T2);
      var labels = node.querySelectorAll('.cbn-shipbar__label');
      if (labels[0]) labels[0].classList.toggle('cbn-shipbar__label--hit', total >= T1);
      if (labels[1]) labels[1].classList.toggle('cbn-shipbar__label--hit', total >= T2);
      var txt = node.querySelector('.cbn-shipbar__txt');
      if (txt) {
        var tpl;
        var left = 0;
        if (total >= T2) {
          tpl = bar.getAttribute('data-tpl-done');
        } else if (total >= T1) {
          tpl = bar.getAttribute('data-tpl-t1');
          left = T2 - total;
        } else {
          tpl = bar.getAttribute('data-tpl-free');
          left = T1 - total;
        }
        var parts = tpl.split('{amt}');
        txt.textContent = '';
        txt.appendChild(document.createTextNode(parts[0]));
        if (parts.length > 1) {
          var bdi = document.createElement('bdi');
          bdi.textContent = fmt(left);
          txt.appendChild(bdi);
          txt.appendChild(document.createTextNode(parts[1]));
        }
      }
      return node;
    }
    function setBubble(count) {
      var icon = document.getElementById('cart-icon-bubble');
      if (!icon) return;
      var bub = icon.querySelector('.cart-count-bubble');
      if (count > 0) {
        if (!bub) {
          bub = document.createElement('div');
          bub.className = 'cart-count-bubble';
          icon.appendChild(bub);
        }
        bub.innerHTML =
          '<span aria-hidden="true">' + (count < 100 ? count : '99+') + '</span>' +
          '<span class="visually-hidden">' + count + '</span>';
      } else if (bub) {
        bub.remove();
      }
    }
    /* round 35: the page's bottom reserve tracks the bar's REAL height (text can
       wrap / zoom can grow it past the old fixed 12rem — which let the bar cover
       the footer). --cbn-stick-h feeds body.cbn-stick-on's padding. */
    function syncPad() {
      if (!bar.classList.contains('cbn-stick--off')) {
        document.documentElement.style.setProperty('--cbn-stick-h', bar.offsetHeight + 'px');
      }
    }
    function render(total, count) {
      var off = !count;
      bar.classList.toggle('cbn-stick--off', off);
      document.body.classList.toggle('cbn-stick-on', !off);
      setBubble(count);
      if (off) return;
      var current = host.querySelector('.cbn-shipbar');
      var next = buildBar(total);
      if (current) current.replaceWith(next);
      else host.appendChild(next);
      syncPad();
    }
    document.body.classList.toggle('cbn-stick-on', !bar.classList.contains('cbn-stick--off'));
    syncPad();
    window.addEventListener('resize', syncPad);
    if (window.ResizeObserver) new ResizeObserver(syncPad).observe(bar);

    var pending = null;
    function refresh() {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart.js' : '/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (c) { render(c.total_price, c.item_count); })
          .catch(function () {});
      }, 250);
    }
    /* any cart mutation through fetch triggers a refresh */
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(refresh, function () {});
      }
      return p;
    };

    /* silent add: Dawn's drawer refreshes its contents but must NOT auto-open */
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('cart-drawer').then(function () {
        var CD = customElements.get('cart-drawer');
        if (!CD || !CD.prototype.renderContents) return;
        var orig = CD.prototype.renderContents;
        CD.prototype.renderContents = function (ps) {
          var self = this;
          var realOpen = self.open;
          self.open = function () {};
          try {
            orig.call(self, ps);
          } finally {
            setTimeout(function () {
              self.open = realOpen;
            }, 400);
          }
        };
      }).catch(function () {});
      customElements.whenDefined('cart-notification').then(function () {
        var CN = customElements.get('cart-notification');
        if (!CN || !CN.prototype.renderContents) return;
        var orig = CN.prototype.renderContents;
        CN.prototype.renderContents = function (ps) {
          var self = this;
          var realOpen = self.open;
          self.open = function () {};
          try {
            orig.call(self, ps);
          } finally {
            setTimeout(function () {
              self.open = realOpen;
            }, 400);
          }
        };
      }).catch(function () {});
    }

    /* hide the sticky bar while the drawer is open — the drawer has its own bar */
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(function () {
        document.body.classList.toggle('cbn-stick-suppress', drawer.classList.contains('active'));
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    /* info popover */
    if (info && pop) {
      info.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = pop.hasAttribute('hidden');
        if (open) pop.removeAttribute('hidden');
        else pop.setAttribute('hidden', '');
        info.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      document.addEventListener('click', function (e) {
        if (!pop.hasAttribute('hidden') && !bar.contains(e.target)) {
          pop.setAttribute('hidden', '');
          info.setAttribute('aria-expanded', 'false');
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !pop.hasAttribute('hidden')) {
          pop.setAttribute('hidden', '');
          info.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  /* ---------- round 33: card add-to-cart (silent — no drawer) ----------
     [data-cbn-card-atc] spans on the homepage cards add a single unit via AJAX;
     the patched fetch above refreshes the sticky bar + header bubble. Multi-
     variant cards carry [data-cbn-qv] instead and open the quick view. */
  function initCardAtc() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cbn-card-atc]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      if (btn.classList.contains('is-busy')) return;
      btn.classList.add('is-busy');
      var root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(btn.getAttribute('data-cbn-card-atc'), 10), quantity: 1 })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          var was = btn.textContent;
          btn.textContent = btn.getAttribute('data-done-txt') || was;
          btn.classList.add('is-done');
          setTimeout(function () {
            btn.textContent = was;
            btn.classList.remove('is-done', 'is-busy');
          }, 1600);
        })
        .catch(function () {
          btn.classList.remove('is-busy');
        });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var btn = e.target && e.target.closest && e.target.closest('[data-cbn-card-atc]');
      if (!btn) return;
      e.preventDefault();
      btn.click();
    });
  }

  /* ---------- round 32: Judge.me empty-widget safeguard ----------
     Some products carry a stub widget metafield (no reviews, still wrapped in
     Judge.me's temp-hiding markup) — e.g. when the app's sync for that product
     is stale. If hydration settles with zero review cards and no Judge.me UI,
     swap in the Carbon "be the first" prompt instead of an empty box. */
  function initJdgmEmptyGuard() {
    var w = document.getElementById('judgeme_product_reviews');
    if (!w) return;
    window.addEventListener('load', function () {
      setTimeout(function () {
        var hasReviews = w.querySelectorAll('.jdgm-rev').length > 0;
        var hasJdgmUi = w.querySelector('.jdgm-rev-widg__actions, .jdgm-widget-actions-wrapper, .jdgm-form');
        var hasFallback = w.querySelector('.cbn-jdgm-first');
        if (hasReviews || hasJdgmUi || hasFallback) return;
        var ar = (document.documentElement.lang || '').indexOf('ar') === 0;
        w.innerHTML =
          '<div class="cbn-jdgm-first">' +
          '<span class="cbn-jdgm-first__stars" aria-hidden="true">' +
          '<span class="jdgm-star jdgm--off"></span><span class="jdgm-star jdgm--off"></span><span class="jdgm-star jdgm--off"></span><span class="jdgm-star jdgm--off"></span><span class="jdgm-star jdgm--off"></span>' +
          '</span>' +
          '<p class="cbn-jdgm-first__txt">' +
          (ar
            ? 'لا توجد تقييمات بعد — كن أول من يشارك تجربته مع هذا المنتج.'
            : 'No reviews yet — be the first to share your experience with this product.') +
          '</p></div>';
      }, 6000);
    }, { once: true });
  }

  /* ---------- round 37: PDP reviews rail — arrows + write-review dialog ----------
     The rail itself is the SAME homepage marquee (initAutoRails/initRails arm it via
     [data-cbn-revs]/.cbn-rail). Arrows scroll one card and borrow the marquee's own
     wheel-hold so autoplay pauses, then resumes. The form POSTs to Judge.me's public
     create-review endpoint — reviews land in the normal moderation queue. */
  function initPdpReviews() {
    var host = document.querySelector('[data-cbn-pdp-revs]');
    if (!host) return;
    var rail = host.querySelector('[data-cbn-revs]');
    host.querySelectorAll('[data-cbn-rev-arrow]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!rail) return;
        var card = rail.querySelector('.cbn-revc');
        var step = card ? card.offsetWidth + 16 : 260;
        var rtl = getComputedStyle(rail).direction === 'rtl';
        var fwd = btn.getAttribute('data-cbn-rev-arrow') === 'next';
        rail.dispatchEvent(new Event('wheel')); /* marquee hold(3500) without drag side-effects */
        rail.scrollBy({ left: (fwd ? 1 : -1) * (rtl ? -1 : 1) * step, behavior: 'smooth' });
      });
    });
    var dlg = host.querySelector('[data-cbn-wr]');
    if (!dlg || !dlg.showModal) return;
    var form = dlg.querySelector('[data-cbn-wr-form]');
    var done = dlg.querySelector('[data-cbn-wr-done]');
    var err = dlg.querySelector('[data-cbn-wr-err]');
    var rating = form.querySelector('input[name="rating"]');
    var stars = Array.prototype.slice.call(dlg.querySelectorAll('.cbn-wr__star'));
    function paint(v) {
      stars.forEach(function (s, i) {
        s.classList.toggle('is-on', i < v);
        s.setAttribute('aria-checked', i + 1 === v ? 'true' : 'false');
      });
    }
    stars.forEach(function (s) {
      s.addEventListener('click', function () {
        rating.value = s.getAttribute('data-v');
        paint(+rating.value);
      });
    });
    host.querySelectorAll('[data-cbn-wr-open]').forEach(function (b) {
      b.addEventListener('click', function () {
        form.hidden = false;
        done.hidden = true;
        err.hidden = true;
        dlg.showModal();
      });
    });
    dlg.querySelectorAll('[data-cbn-wr-close]').forEach(function (b) {
      b.addEventListener('click', function () {
        dlg.close();
      });
    });
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) dlg.close(); /* backdrop click */
    });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      err.hidden = true;
      var f = new FormData(form);
      if (!rating.value || !f.get('name') || !f.get('email') || !f.get('body') || !form.querySelector('[name="email"]').checkValidity()) {
        err.textContent = dlg.querySelector('[data-cbn-wr-msg-invalid]').textContent;
        err.hidden = false;
        return;
      }
      var btn = form.querySelector('.cbn-wr__submit');
      btn.disabled = true;
      /* FormData like Judge.me's own widget — a "simple" request, so no CORS
         preflight (their api host 404s OPTIONS; a JSON post would be blocked) */
      var fd = new FormData();
      fd.append('shop_domain', host.getAttribute('data-shop-domain'));
      fd.append('platform', 'shopify');
      fd.append('id', host.getAttribute('data-product-eid'));
      fd.append('name', f.get('name'));
      fd.append('email', f.get('email'));
      fd.append('rating', rating.value);
      fd.append('title', f.get('title') || '');
      fd.append('body', f.get('body'));
      fetch('https://api.judge.me/api/v1/reviews', { method: 'POST', body: fd })
        .then(function (r) {
          if (r.ok) {
            form.hidden = true;
            done.hidden = false;
            return;
          }
          return r.json().then(function (j) {
            throw new Error(j && j.message ? j.message : '');
          }, function () {
            throw new Error('');
          });
        })
        .catch(function (ex) {
          err.textContent = ex && ex.message ? ex.message : dlg.querySelector('[data-cbn-wr-msg-fail]').textContent;
          err.hidden = false;
        })
        .then(function () {
          btn.disabled = false;
        });
    });
  }

  /* ---------- round 39: FULL review lists — no cap, no duplication ----------
     Data comes from Judge.me's own paginated feeds on cdn.judge.me (the same ones
     its widget uses; plain GETs, CORS-open, no token):
       reviews_for_widget      → one product's reviews as JSON (25/page)
       all_reviews_js_based    → the shop-wide stream as widget HTML, split into
                                 DISJOINT review_type feeds: 'product-reviews' /
                                 'shop-reviews' — so cross-stream duplication is
                                 structurally impossible.
     Expansion always REPLACES what the server painted (never appends alongside it),
     then appends further pages — a review can never render twice. All text lands
     via textContent (no third-party HTML is ever injected live). */
  function initRevLoaders() {
    var CDN = 'https://cdn.judge.me/reviews/';
    function qs(o) {
      return Object.keys(o).map(function (k) {
        return k + '=' + encodeURIComponent(o[k]);
      }).join('&');
    }
    function pad(n) {
      return (n < 10 ? '0' : '') + n;
    }
    function fmtDate(s) {
      var d = new Date(s);
      if (isNaN(d)) return '';
      return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear();
    }
    function el(tag, cls, txt) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (txt != null) n.textContent = txt;
      return n;
    }
    function starRow(rating, of5) {
      var w = el('div', 'cbn-revc__stars');
      w.setAttribute('aria-label', rating + ' ' + (of5 || ''));
      for (var i = 1; i <= 5; i++) {
        w.appendChild(el('span', 'cbn-revc__star' + (i > rating ? ' cbn-revc__star--off' : ''), '★'));
      }
      return w;
    }
    function buildCard(d, labels, showProduct) {
      var a = el('article', 'cbn-revc cbn-card');
      if (d.pic) {
        var ph = el('div', 'cbn-revc__photo');
        var im = document.createElement('img');
        im.src = d.pic;
        im.alt = '';
        im.loading = 'lazy';
        im.width = 400;
        im.height = 400;
        ph.appendChild(im);
        a.appendChild(ph);
      }
      var b = el('div', 'cbn-revc__body');
      b.appendChild(starRow(d.rating, labels.of5));
      if (d.title) b.appendChild(el('h3', 'cbn-revc__title', d.title));
      if (d.body) {
        var q = el('blockquote', 'cbn-revc__quote');
        var t = d.body;
        if (t.length > 150) t = t.slice(0, 147) + '…';
        q.appendChild(el('p', 'cbn-revc__text', t));
        b.appendChild(q);
      }
      var f = el('footer', 'cbn-revc__meta');
      f.appendChild(el('span', 'cbn-revc__avatar', d.initial || '★'));
      var who = el('span', 'cbn-revc__who');
      who.appendChild(el('span', 'cbn-revc__name', d.name || ''));
      if (d.date) who.appendChild(el('time', 'cbn-revc__date', d.date));
      f.appendChild(who);
      if (d.verified) f.appendChild(el('span', 'cbn-revc__verified', '✓ ' + (labels.verified || '')));
      b.appendChild(f);
      if (showProduct && d.productTitle) {
        var pr = el('div', 'cbn-revc__product');
        if (d.productUrl && d.productUrl.indexOf('/') === 0) {
          var ln = el('a', null, d.productTitle);
          ln.href = d.productUrl.split('#')[0];
          pr.appendChild(ln);
        } else {
          pr.appendChild(el('span', null, d.productTitle));
        }
        b.appendChild(pr);
      }
      a.appendChild(b);
      return a;
    }
    function fromJson(r) {
      var pic = r.pictures_urls && r.pictures_urls[0];
      return {
        rating: +r.rating || 0,
        title: r.title || '',
        body: (r.body || '').trim(),
        name: r.reviewer_name || '',
        initial: r.reviewer_initial || '',
        date: fmtDate(r.created_at),
        verified: !!r.verified_buyer,
        pic: (pic && (pic.compact || pic.small || pic.original)) || null,
        productTitle: r.product_title || '',
        productUrl: r.product_url || ''
      };
    }
    function fromBlock(n) {
      var g = function (sel) {
        var e = n.querySelector(sel);
        return e ? e.textContent.trim() : '';
      };
      var rEl = n.querySelector('.jdgm-rev__rating');
      var link = n.querySelector('.jdgm-rev__prod-link');
      var pic = n.querySelector('.jdgm-rev__pics img');
      var ts = n.querySelector('.jdgm-rev__timestamp');
      return {
        rating: rEl ? +rEl.getAttribute('data-score') || 0 : 0,
        title: g('.jdgm-rev__title'),
        body: g('.jdgm-rev__body'),
        name: g('.jdgm-rev__author'),
        initial: g('.jdgm-rev__icon'),
        date: ts ? fmtDate((ts.getAttribute('data-content') || '').replace(' UTC', 'Z').replace(' ', 'T')) : '',
        verified: n.getAttribute('data-verified-buyer') === 'true',
        pic: pic ? pic.getAttribute('src') || pic.getAttribute('data-src') : null,
        productTitle: link ? link.textContent.trim() : '',
        productUrl: link ? link.getAttribute('href') || '' : ''
      };
    }

    /* PDP «عرض كل التقييمات»: fetch every page, then rebuild the rail from scratch
       (node replacement also cleanly kills the marquee's clones + rAF loop). */
    var pdp = document.querySelector('[data-cbn-pdp-revs]');
    var moreBtn = pdp && pdp.querySelector('[data-cbn-rev-more]');
    if (pdp && moreBtn) {
      moreBtn.addEventListener('click', function () {
        if (moreBtn.classList.contains('is-busy')) return;
        moreBtn.classList.add('is-busy');
        var labels = { verified: pdp.getAttribute('data-l-verified'), of5: pdp.getAttribute('data-l-of5') };
        var dom = pdp.getAttribute('data-shop-domain');
        var params = { url: dom, shop_domain: dom, platform: 'shopify', product_id: pdp.getAttribute('data-product-eid'), per_page: 25 };
        var all = [];
        function fetchPage(p) {
          return fetch(CDN + 'reviews_for_widget?' + qs(params) + '&page=' + p)
            .then(function (r) {
              if (!r.ok) throw new Error(r.status);
              return r.json();
            })
            .then(function (d) {
              all = all.concat(d.reviews || []);
              var tp = (d.pagination && d.pagination.total_pages) || 1;
              return p < tp && p < 40 ? fetchPage(p + 1) : all;
            });
        }
        fetchPage(1)
          .then(function (revs) {
            var oldRail = pdp.querySelector('[data-cbn-revs]');
            var sum = oldRail.querySelector('.cbn-revc--sum');
            var fresh = oldRail.cloneNode(false);
            fresh.removeAttribute('data-cbn-auto');
            fresh.classList.remove('cbn-auto');
            if (sum) fresh.appendChild(sum);
            revs.forEach(function (r) {
              fresh.appendChild(buildCard(fromJson(r), labels, false));
            });
            oldRail.replaceWith(fresh); /* the marquee loop stops itself: !rail.isConnected */
            attachRailDrag(fresh);
            moreBtn.hidden = true;
            var note = pdp.querySelector('[data-cbn-rev-note]');
            if (note) note.hidden = true;
          })
          .catch(function () {
            moreBtn.classList.remove('is-busy');
            moreBtn.textContent = pdp.getAttribute('data-l-fail');
          });
      });
    }

    /* store reviews page: each stream expands to its FULL disjoint feed (24/page).
       Judge.me cross-posts one review to every product in a product group (each copy
       its own id) — the page's standing rule collapses those to ONE card by content
       key (name ¦ rating ¦ body head), and the other stream's rendered cards count
       as occupied too, so a review can never show twice anywhere on the page. */
    function contentKey(name, rating, body) {
      return (name || '') + '¦' + (+rating || 0) + '¦' + (body || '').slice(0, 60);
    }
    function domKey(c) {
      var name = c.querySelector('.cbn-revc__name');
      var body = c.querySelector('.cbn-revc__text');
      var stars = c.querySelectorAll('.cbn-revc__star:not(.cbn-revc__star--off)').length;
      return contentKey(name ? name.textContent : '', stars, body ? body.textContent : '');
    }
    document.querySelectorAll('[data-cbn-revpage-more]').forEach(function (btn) {
      var type = btn.getAttribute('data-review-type');
      var grid = document.querySelector('[data-cbn-revgrid="' + type + '"]');
      if (!grid) {
        btn.hidden = true;
        return;
      }
      var page = 0,
        expanded = false,
        total = null, /* the feed reports totals on page 1 only */
        seen = new Set(); /* content keys already appended to THIS stream */
      var labels = { verified: btn.getAttribute('data-l-verified'), of5: btn.getAttribute('data-l-of5') };
      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-busy')) return;
        btn.classList.add('is-busy');
        var dom = btn.getAttribute('data-shop-domain');
        fetch(CDN + 'all_reviews_js_based?' + qs({ url: dom, shop_domain: dom, platform: 'shopify', review_type: type, per_page: 24, page: page + 1 }))
          .then(function (r) {
            if (!r.ok) throw new Error(r.status);
            return r.json();
          })
          .then(function (d) {
            page += 1;
            var t = type === 'shop-reviews' ? d.number_of_shop_reviews : d.number_of_product_reviews;
            if (t != null) total = t;
            var holder = document.createElement('div');
            holder.innerHTML = d.html || ''; /* detached parse only — never inserted live */
            var blocks = holder.querySelectorAll('.jdgm-rev');
            if (!expanded) {
              grid.textContent = '';
              expanded = true;
            }
            var occupied = new Set(seen);
            var other = document.querySelector('[data-cbn-revgrid="' + (type === 'shop-reviews' ? 'product-reviews' : 'shop-reviews') + '"]');
            if (other) other.querySelectorAll('.cbn-revc').forEach(function (c) { occupied.add(domKey(c)); });
            blocks.forEach(function (n) {
              var d = fromBlock(n);
              var k = contentKey(d.name, d.rating, d.body);
              if (occupied.has(k)) return; /* cross-posted / cross-stream copy */
              occupied.add(k);
              seen.add(k);
              grid.appendChild(buildCard(d, labels, type === 'product-reviews'));
            });
            btn.classList.remove('is-busy');
            /* done when: empty page, short page (< the 24 we ask for), or total reached */
            if (!blocks.length || blocks.length < 24 || (total != null && grid.children.length >= total)) btn.hidden = true;
          })
          .catch(function () {
            btn.classList.remove('is-busy');
            btn.textContent = btn.getAttribute('data-l-fail');
          });
      });
    });
  }

  /* ---------- inner-page enrichment: reveals + FAQ accordion ---------- */
  function initPageEnrich() {
    var wrap = document.querySelector('.cbn-page');
    if (!wrap) return;
    var rte = wrap.querySelector('.rte');
    if (!rte) return;
    var title = (wrap.querySelector('.main-page-title') || {}).textContent || '';
    var isFaq = /أسئلة|FAQ/i.test(title);
    if (isFaq) {
      rte.classList.add('cbn-faq-rte'); /* styles the h3 group headings */
      var paras = Array.prototype.slice.call(rte.querySelectorAll(':scope > p'));
      paras.forEach(function (p) {
        var strong = p.querySelector('strong');
        if (!strong || !strong.textContent.trim()) return;
        var q = strong.textContent.trim();
        var det = document.createElement('details');
        det.className = 'cbn-acc-item';
        var sum = document.createElement('summary');
        sum.textContent = q;
        var body = document.createElement('div');
        body.className = 'cbn-acc-body';
        var clone = p.cloneNode(true);
        var cs = clone.querySelector('strong');
        if (cs) cs.remove();
        while (clone.firstChild && ((clone.firstChild.nodeType === 3 && !clone.firstChild.textContent.trim()) || (clone.firstChild.nodeType === 1 && clone.firstChild.tagName === 'BR'))) {
          clone.firstChild.remove();
        }
        if (!clone.textContent.trim()) return; /* question-only paragraph — leave as-is */
        body.appendChild(clone);
        det.appendChild(sum);
        det.appendChild(body);
        p.replaceWith(det);
      });
      /* FAQPage JSON-LD is server-rendered in sections/main-page.liquid (no JS copy — avoids a duplicate) */
    }
    /* scroll reveals for page blocks, consistent with the homepage */
    Array.prototype.slice.call(rte.children).forEach(function (child) {
      child.classList.add('cbn-rv');
    });
  }

  /* ---------- parallax depth: key imagery drifts at slightly different speeds (desktop, subtle) ---------- */
  function initParallax() {
    if (!desktop || reduced || !hasGsap) return;
    var targets = [
      { sel: '.cbn-mood img', y: -8 },
      { sel: '.cbn-cats .cbn-frame img', y: 6 },
      { sel: '.cbn-acc .cbn-frame img', y: 5 },
    ];
    targets.forEach(function (t) {
      document.querySelectorAll(t.sel).forEach(function (el) {
        el.classList.add('cbn-par');
        gsap.fromTo(
          el,
          { yPercent: -t.y },
          {
            yPercent: t.y,
            ease: 'none',
            scrollTrigger: { trigger: el.closest('.shopify-section') || el, start: 'top bottom', end: 'bottom top', scrub: true },
          }
        );
      });
    });
  }

  /* ---------- burning scroll progress: ember fuse under the announcement bar ----------
     rAF-throttled scroll handler, transform-only (scaleX fill + translateX tip), RTL fills from the right. */
  function initFuse() {
    if (reduced) return;
    var bar = document.createElement('div');
    bar.className = 'cbn-fuse';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML = '<div class="cbn-fuse__fill"></div><div class="cbn-fuse__tip"></div>';
    document.body.appendChild(bar);
    var fill = bar.firstChild;
    var tip = bar.lastChild;
    var rtl = document.documentElement.dir === 'rtl';
    var max = 0,
      width = 0,
      ticking = false;
    function update() {
      ticking = false;
      if (max <= 0) return;
      var p = Math.min(1, Math.max(0, (window.scrollY || document.documentElement.scrollTop || 0) / max));
      fill.style.transform = 'scaleX(' + p + ')';
      tip.style.transform = 'translateX(' + (rtl ? -p * width : p * width) + 'px)';
      tip.style.opacity = p > 0.004 ? 1 : 0;
    }
    function measure() {
      max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      width = bar.clientWidth;
      bar.classList.toggle('cbn-off', max < 200); /* nothing to burn on short pages */
      update();
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    var rT;
    window.addEventListener('resize', function () {
      clearTimeout(rT);
      rT = setTimeout(measure, 200);
    });
    window.addEventListener('load', measure, { once: true });
    measure();
  }

  /* ---------- card entrance: simple fade + rise (owner request — the 3D tilt is gone) ----------
     FAIL-SAFE: cards are never pre-hidden. gsap.from inside onEnter hides + animates in the
     same frame the card enters; if the batch never fires the card just stays visible.
     Claims its cards from the generic reveal pass and Dawn's animations.js. */
  function initTiltIn() {
    if (reduced || !hasGsap) return;
    var cards = Array.prototype.slice.call(document.querySelectorAll('.cbn-card'));
    /* Dawn collection/search grids: take over the li wrappers. Dawn's own scroll
       animations are disabled in settings (they pre-hide content — fail-safe violation),
       but strip the classes defensively in case the setting ever flips back. */
    document.querySelectorAll('.grid__item').forEach(function (li) {
      if (!li.querySelector('.card-wrapper')) return;
      li.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen', 'scroll-trigger--cancel');
      cards.push(li);
    });
    cards = cards.filter(function (c) {
      /* skip hidden contexts + the size-compare cards (they own their disc/clock choreography) */
      return c.offsetParent !== null && !c.hasAttribute('data-cbn-sc-card');
    });
    if (!cards.length) return;
    cards.forEach(function (c) {
      c.classList.remove('cbn-rv');
      c.classList.add('cbn-in');
    });
    /* rails: the wrapper's own fade would double up — the cards carry the entrance now */
    document.querySelectorAll('.cbn-rail-clip.cbn-rv').forEach(function (w) {
      w.classList.remove('cbn-rv');
      w.classList.add('cbn-in');
    });
    var lift = desktop ? 26 : 20;
    ScrollTrigger.batch(cards, {
      start: 'top 97%',
      once: true,
      onEnter: function (batch) {
        batch.forEach(function (c) {
          c.classList.add('cbn-tilting'); /* suspends CSS hover transitions while GSAP owns the transform */
        });
        gsap.from(batch, {
          opacity: 0,
          y: lift,
          duration: 0.65,
          ease: 'power2.out',
          stagger: 0.06,
          clearProps: 'transform,opacity', /* hand the transform back to the CSS hover lift */
          onComplete: function () {
            batch.forEach(function (c) {
              c.classList.remove('cbn-tilting');
            });
          },
        });
      },
    });
  }

  /* (the drifting spark/smoke-mote layer was removed on owner request 2026-07-24 — may return later) */

  /* (round 8: the «أشعل الفحمة» ignite interactive was removed everywhere on owner request) */

  /* ---------- «ليش كاربون» video: lazy attach + play only while on screen ----------
     Sources ship with data-src and are attached only when the clip comes NEAR the viewport
     (round 40 — was: right after window load), so it never loads for visitors who don't
     scroll that far. Reduced-motion / Save-Data keep the poster frame only. */
  function initWhyVideo() {
    var vid = document.querySelector('video[data-cbn-why-video]');
    if (!vid || reduced || saveData) return;
    var attached = false;
    function attach() {
      if (attached) return;
      attached = true;
      vid.querySelectorAll('source[data-src]').forEach(function (s) {
        s.src = s.dataset.src;
      });
      vid.load();
    }
    function arm() {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              attach();
              var pp = vid.play();
              if (pp && pp.catch) pp.catch(function () {});
            } else if (attached) {
              vid.pause();
            }
          });
        },
        { rootMargin: '600px 0px' }
      ).observe(vid);
    }
    if (document.readyState === 'complete') {
      arm();
    } else {
      window.addEventListener('load', arm, { once: true });
    }
  }

  /* ---------- editorial PDP: qty stepper + gallery thumb swap ---------- */
  function initEditPdp() {
    var hero = document.getElementById('cbn-edit-hero');
    if (!hero) return;
    hero.querySelectorAll('[data-cbn-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var inp = btn.parentElement.querySelector('input[name="quantity"]');
        if (!inp) return;
        var v = Math.max(1, (parseInt(inp.value, 10) || 1) + parseInt(btn.getAttribute('data-cbn-qty'), 10));
        inp.value = v;
        inp.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    /* gallery: clicking a thumbnail swaps the main image (src + srcset) */
    var main = document.getElementById('cbn-edit-main-img');
    var thumbs = hero.querySelectorAll('[data-cbn-thumb]');
    if (main && thumbs.length) {
      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          main.src = t.getAttribute('data-full');
          main.srcset = t.getAttribute('data-srcset');
          thumbs.forEach(function (o) {
            o.classList.toggle('is-active', o === t);
          });
        });
      });
    }
  }

  /* ---------- collection pages: one-line intro + «اقرأ المزيد» expander ----------
     The button only appears when the description actually overflows its one-line clamp. */
  function initColIntro() {
    document.querySelectorAll('[data-cbn-colintro]').forEach(function (wrap) {
      var body = wrap.querySelector('.cbn-colintro__body');
      var btn = wrap.querySelector('[data-cbn-colintro-btn]');
      if (!body || !btn) return;
      function measure() {
        if (wrap.classList.contains('is-open')) return;
        btn.hidden = body.scrollHeight <= body.clientHeight + 2;
      }
      measure();
      window.addEventListener('resize', measure);
      btn.addEventListener('click', function () {
        var open = wrap.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.querySelector('[data-more]').hidden = open;
        btn.querySelector('[data-less]').hidden = !open;
      });
    });
  }

  /* ---------- cart tier celebration: one premium moment per crossed tier ----------
     Tier 1 = free shipping (≥10 KWD), tier 2 = 5% off (≥15 KWD). The shipbar snippet
     re-renders with its host cart section on every cart change; we read the tier off its
     modifier classes, compare with the last tier seen this session, and play a soft
     glow + sparkle burst + checkmark over the bar when the tier goes UP. Going down
     (item removed) just re-arms silently. Nothing plays under prefers-reduced-motion. */
  function initTierCelebrate() {
    var KEY = 'cbn-tier';

    function readTier() {
      var bar = document.querySelector('.cbn-shipbar');
      if (!bar) return 0; /* empty cart renders no bar */
      if (bar.classList.contains('cbn-shipbar--done')) return 2;
      if (bar.classList.contains('cbn-shipbar--t1')) return 1;
      return 0;
    }
    function stored() {
      try { var v = sessionStorage.getItem(KEY); return v === null ? null : parseInt(v, 10) || 0; } catch (e) { return null; }
    }
    function store(t) {
      try { sessionStorage.setItem(KEY, String(t)); } catch (e) {}
    }

    function burst(bar, tier) {
      if (bar.querySelector('.cbn-cel')) return; /* one moment at a time */
      var cel = document.createElement('div');
      cel.className = 'cbn-cel' + (tier >= 2 ? ' cbn-cel--t2' : '');
      cel.setAttribute('aria-hidden', 'true');
      var html =
        '<span class="cbn-cel__glow"></span>' +
        '<span class="cbn-cel__ring"></span>' +
        '<span class="cbn-cel__badge"><svg viewBox="0 0 24 24" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>';
      for (var i = 0; i < 10; i++) {
        var a = Math.round((360 / 10) * i + (Math.random() * 22 - 11));
        var d = 3.4 + Math.random() * 2.6; /* rem */
        var t = (0.9 + Math.random() * 0.5).toFixed(2);
        var s = (0.55 + Math.random() * 0.6).toFixed(2);
        html += '<i class="cbn-cel__sp" style="--a:' + a + 'deg;--d:' + d.toFixed(2) + 'rem;--t:' + t + 's;--s:' + s + '">✦</i>';
      }
      cel.innerHTML = html;
      bar.classList.add('cbn-cel-on');
      bar.appendChild(cel);
      setTimeout(function () {
        cel.remove();
        bar.classList.remove('cbn-cel-on');
      }, 1900);
    }

    function check() {
      var tier = readTier();
      var last = stored();
      if (last === null) { store(tier); return; } /* first sight this session: arm, don't pop */
      if (tier > last && !reduced) {
        document.querySelectorAll('.cbn-shipbar').forEach(function (bar) {
          if (bar.offsetParent !== null || bar.closest('cart-drawer')) burst(bar, tier);
        });
      }
      if (tier !== last) store(tier);
    }

    check();
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        check();
      }, 120);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* ---------- size comparison: entrance stagger + burn-clock fills (GSAP; static without it) ---------- */
  function initSizeCompare() {
    var grid = document.querySelector('[data-cbn-sc]');
    if (!grid) return;

    /* click / keyboard select — single-select highlight; the product link still navigates */
    grid.addEventListener('click', function (e) {
      if (e.target.closest('a')) return;
      var card = e.target.closest('[data-cbn-sc-card]');
      if (!card) return;
      var was = card.classList.contains('is-active');
      grid.querySelectorAll('[data-cbn-sc-card].is-active').forEach(function (c) {
        c.classList.remove('is-active');
      });
      if (!was) card.classList.add('is-active');
    });
    grid.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = e.target.closest && e.target.closest('[data-cbn-sc-card]');
      if (!card || e.target.closest('a')) return;
      e.preventDefault();
      card.click();
    });

    /* No GSAP or reduced motion → leave everything in its server-rendered final state. */
    if (!hasGsap || reduced) return;

    var cards = grid.querySelectorAll('[data-cbn-sc-card]');
    var clocks = grid.querySelectorAll('[data-cbn-sc-fill]');

    /* FAIL-SAFE: nothing is rewound/hidden at boot — every from/fromTo below carries
       immediateRender:false, so the server-rendered final state stays visible until
       the trigger actually plays. */
    var tl = gsap.timeline({
      scrollTrigger: { trigger: grid, start: 'top 88%', once: true }
    });
    tl.from(cards, { autoAlpha: 0, y: 26, duration: 0.55, ease: 'power2.out', stagger: 0.09, immediateRender: false }, 0);
    if (desktop) {
      /* desktop-rich: discs pop with a slight overshoot */
      tl.from(grid.querySelectorAll('[data-cbn-sc-disc]'), {
        scale: 0.55,
        duration: 0.7,
        ease: 'back.out(1.6)',
        stagger: 0.09,
        immediateRender: false
      }, 0.1);
    }
    clocks.forEach(function (c, i) {
      var bar = c.querySelector('.cbn-sc-clock__bar');
      var fill = parseFloat(c.getAttribute('data-cbn-sc-fill')) || 0;
      if (!bar) return;
      tl.fromTo(bar, { attr: { 'stroke-dasharray': '0 100' } }, {
        attr: { 'stroke-dasharray': fill + ' 100' },
        duration: 1.1,
        ease: 'power2.out',
        immediateRender: false
      }, 0.35 + i * 0.09);
      /* desktop hover: replay the fill */
      if (desktop) {
        var card = c.closest('[data-cbn-sc-card]');
        if (card) {
          card.addEventListener('mouseenter', function () {
            gsap.fromTo(bar, { attr: { 'stroke-dasharray': '0 100' } }, {
              attr: { 'stroke-dasharray': fill + ' 100' },
              duration: 0.9,
              ease: 'power2.out',
              overwrite: 'auto'
            });
          });
        }
      }
    });
  }

  /* (the reviews rail's own entrance is gone — its cards are .cbn-card, so the fail-safe
     tilt-in already owns them; a second tween here would double-animate and re-hide.) */

  /* ---------- size lineup helpers (shared by the PDP strip snippet's JS twin + quick view) ----------
     Word markers mirror snippets/carbon-size-lineup.liquid; core handles mirror the PDP block
     defaults (primary handles — Shopify localizes the URL on navigation). */
  var SLU_SIZES = [
    { key: 'petite', mm: 22, ar: 'بيتيت', en: 'Petite', handle: 'petite-premium-charcoal-22mm-80-pcs', marks: ['22mm', '22 mm', 'petite', 'بيتيت'] },
    { key: 'classic', mm: 33, ar: 'كلاسيك', en: 'Classic', handle: 'premium-charcoal-100-pcs', marks: ['33mm', '33 mm', 'classic', 'كلاسيك'] },
    { key: 'max', mm: 40, ar: 'ماكس', en: 'Max', handle: 'max-premium-charcoal-40mm-60-pcs', marks: ['40mm', '40 mm', 'max', 'ماكس'] },
    { key: 'mega', mm: 50, ar: 'ميقا', en: 'Mega', handle: 'mega-premium-charcoal-50mm-60-pcs', marks: ['50mm', '50 mm', 'mega', 'ميقا'] },
  ];
  function sluDetect(hay) {
    hay = (hay || '').toLowerCase();
    for (var i = 0; i < SLU_SIZES.length; i++) {
      for (var j = 0; j < SLU_SIZES[i].marks.length; j++) {
        if (hay.indexOf(SLU_SIZES[i].marks[j]) !== -1) return SLU_SIZES[i].key;
      }
    }
    return '';
  }
  function sluStripHTML(currentKey) {
    var ar = (document.documentElement.lang || '').indexOf('ar') === 0;
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var html = '<div class="cbn-slu cbn-slu--qv" aria-label="' + (ar ? 'أحجام عائلة الفحم' : 'Charcoal size family') + '">';
    html += '<p class="cbn-slu__h">' + (ar ? 'الحجم ضمن العائلة' : 'Size in the lineup') + '</p>';
    html += '<div class="cbn-slu__row">';
    SLU_SIZES.forEach(function (s) {
      var inner =
        '<span class="cbn-slu__stage"><span class="cbn-sc-disc" style="--mm: ' + s.mm + '" aria-hidden="true"></span></span>' +
        '<span class="cbn-slu__dot" aria-hidden="true"></span>' +
        '<span class="cbn-slu__name">' + (ar ? s.ar : s.en) + '</span>';
      if (s.key === currentKey) {
        html += '<span class="cbn-slu__item is-cur" aria-current="true">' + inner + '</span>';
      } else if (s.handle) {
        html += '<a class="cbn-slu__item" href="' + root + 'products/' + s.handle + '">' + inner + '</a>';
      } else {
        html += '<span class="cbn-slu__item is-off">' + inner + '</span>';
      }
    });
    html += '</div></div>';
    return html;
  }

  /* ---------- shoppable scene: hotspots over the lifestyle image ---------- */
  function initShopSpots() {
    var scene = document.querySelector('[data-cbn-shopscene]');
    if (!scene) return;
    function closeAll(except) {
      scene.querySelectorAll('[data-cbn-spot-pop]').forEach(function (pop) {
        if (pop !== except) {
          pop.hidden = true;
          var btn = pop.parentElement.querySelector('[data-cbn-spot]');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        }
      });
    }
    scene.addEventListener('click', function (e) {
      var atc = e.target.closest('[data-cbn-spot-atc]');
      if (atc) {
        atc.disabled = true;
        var prev = atc.textContent;
        fetch(((window.routes && routes.cart_add_url) || '/cart/add') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(atc.getAttribute('data-cbn-spot-atc'), 10), quantity: 1, sections: 'cart-drawer,cart-icon-bubble' }),
        })
          .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
          .then(function (res) {
            if (!res.ok) throw new Error('add failed');
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.renderContents === 'function' && res.j.sections) {
              closeAll();
              drawer.renderContents(res.j);
            } else {
              atc.textContent = prev;
            }
            atc.disabled = false;
          })
          .catch(function () {
            atc.disabled = false;
            atc.textContent = prev;
          });
        return;
      }
      var btn = e.target.closest('[data-cbn-spot]');
      if (btn) {
        var pop = btn.parentElement.querySelector('[data-cbn-spot-pop]');
        var opening = pop.hidden;
        closeAll(opening ? pop : null);
        pop.hidden = !opening;
        btn.setAttribute('aria-expanded', opening ? 'true' : 'false');
        if (opening) {
          /* keep the card inside the stage: flip to the other side near the edges */
          var wrap = btn.parentElement;
          var x = parseFloat(wrap.style.left) || 50;
          var y = parseFloat(wrap.style.top) || 50;
          pop.classList.toggle('cbn-shop-pop--start', x > 62);
          pop.classList.toggle('cbn-shop-pop--up', y > 64);
        }
        return;
      }
      if (!e.target.closest('[data-cbn-spot-pop]')) closeAll();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });
    document.addEventListener('click', function (e) {
      if (!scene.contains(e.target)) closeAll();
    });
  }

  /* ---------- quick view ---------- */
  function initQuickView() {
    var qv = document.getElementById('cbn-qv');
    if (!qv) return;
    var body = qv.querySelector('[data-cbn-qv-body]');
    var spinnerHTML = body.innerHTML;
    var lastFocus = null;

    function money(minor) {
      /* this store's JS cart money is 1/100 KWD units (15 KWD = 1500) */
      return (minor / 100).toFixed(3);
    }
    function esc(s) {
      var d = document.createElement('div');
      d.textContent = s == null ? '' : String(s);
      return d.innerHTML;
    }
    function imgUrl(src, w) {
      try {
        var u = new URL(src, location.origin);
        u.searchParams.set('width', w);
        return u.href;
      } catch (e) { return src; }
    }
    function open() {
      lastFocus = document.activeElement;
      qv.hidden = false;
      document.documentElement.style.overflow = 'hidden';
    }
    function close() {
      qv.hidden = true;
      body.innerHTML = spinnerHTML;
      document.documentElement.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function render(p, url) {
      var cur = qv.dataset.lCur;
      var v0 = null;
      for (var i = 0; i < p.variants.length; i++) { if (p.variants[i].available) { v0 = p.variants[i]; break; } }
      v0 = v0 || p.variants[0];
      var descText = '';
      if (p.description) {
        var tmp = document.createElement('div');
        tmp.innerHTML = p.description;
        descText = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
        if (descText.length > 180) descText = descText.slice(0, 180) + '…';
      }
      var html = '<div class="cbn-qv__grid">';
      html += '<div class="cbn-qv__img">' + (p.featured_image ? '<img src="' + esc(imgUrl(p.featured_image, 600)) + '" alt="' + esc(p.title) + '" width="600" height="750" loading="eager">' : '') + '</div>';
      html += '<div class="cbn-qv__info">';
      html += '<h3 class="cbn-qv__title">' + esc(p.title) + '</h3>';
      html += '<div class="cbn-qv__price" data-qv-price></div>';
      if (descText) html += '<p class="cbn-qv__desc">' + esc(descText) + '</p>';
      if (p.variants.length > 1) {
        html += '<select class="cbn-qv__variants" data-qv-variants aria-label="Options">';
        for (var j = 0; j < p.variants.length; j++) {
          var v = p.variants[j];
          html += '<option value="' + v.id + '"' + (v.id === v0.id ? ' selected' : '') + (v.available ? '' : ' disabled') + '>' + esc(v.title) + (v.available ? '' : ' — ' + esc(qv.dataset.lSoldout)) + '</option>';
        }
        html += '</select>';
      }
      html += '<button type="button" class="cbn-btn cbn-btn--primary cbn-qv__atc" data-qv-atc></button>';
      /* charcoal size/pack products carry the size-lineup strip inside the quick view too */
      var qvSize = sluDetect((p.title || '') + ' ' + (p.handle || ''));
      if (qvSize) html += sluStripHTML(qvSize);
      html += '<a class="cbn-qv__link" href="' + esc(url) + '">' + (document.documentElement.lang.indexOf('ar') === 0 ? 'كل التفاصيل' : 'View full details') + '</a>';
      html += '<div class="cbn-qv__err" data-qv-err hidden></div>';
      html += '</div></div>';
      body.innerHTML = html;

      var priceEl = body.querySelector('[data-qv-price]');
      var atc = body.querySelector('[data-qv-atc]');
      var sel = body.querySelector('[data-qv-variants]');
      var err = body.querySelector('[data-qv-err]');
      function variantById(id) {
        for (var k = 0; k < p.variants.length; k++) if (p.variants[k].id === id) return p.variants[k];
        return null;
      }
      function sync(v) {
        var ph = '<bdi>' + money(v.price) + '</bdi><span class="cbn-cur">' + esc(cur) + '</span>';
        if (v.compare_at_price && v.compare_at_price > v.price) ph += '<span class="cbn-was"><bdi>' + money(v.compare_at_price) + '</bdi></span>';
        priceEl.innerHTML = ph;
        atc.textContent = v.available ? qv.dataset.lAdd : qv.dataset.lSoldout;
        atc.disabled = !v.available;
        atc.dataset.variant = v.id;
      }
      sync(v0);
      if (sel) sel.addEventListener('change', function () { var v = variantById(parseInt(sel.value, 10)); if (v) sync(v); });
      atc.addEventListener('click', function () {
        if (atc.disabled) return;
        atc.disabled = true;
        atc.textContent = qv.dataset.lAdding;
        err.hidden = true;
        fetch((window.routes && routes.cart_add_url ? routes.cart_add_url : '/cart/add') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(atc.dataset.variant, 10), quantity: 1, sections: 'cart-drawer,cart-icon-bubble' })
        }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); }).then(function (res) {
          if (!res.ok) throw new Error(res.j && res.j.description || 'add failed');
          atc.textContent = qv.dataset.lAdded;
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function' && res.j.sections) {
            close();
            drawer.renderContents(res.j);
          } else {
            var bubble = document.getElementById('cart-icon-bubble');
            if (bubble && res.j.sections && res.j.sections['cart-icon-bubble']) {
              bubble.innerHTML = new DOMParser().parseFromString(res.j.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section').innerHTML;
            }
            setTimeout(function () { atc.disabled = false; atc.textContent = qv.dataset.lAdd; }, 1600);
          }
        }).catch(function () {
          err.textContent = qv.dataset.lError;
          err.hidden = false;
          atc.disabled = false;
          atc.textContent = qv.dataset.lAdd;
        });
      });
    }
    function launch(handle) {
      open();
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(root + 'products/' + handle + '.js').then(function (r) {
        if (!r.ok) throw new Error('fetch failed');
        return r.json();
      }).then(function (p) {
        render(p, p.url || root + 'products/' + handle);
      }).catch(function () {
        close();
        location.href = root + 'products/' + handle; /* graceful fallback: go to the PDP */
      });
    }
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cbn-qv]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        launch(btn.getAttribute('data-cbn-qv'));
        return;
      }
      if (!qv.hidden && e.target.closest('[data-cbn-qv-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      var btn = e.target.closest && e.target.closest('span[data-cbn-qv]');
      if (btn && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();
        launch(btn.getAttribute('data-cbn-qv'));
        return;
      }
      if (e.key === 'Escape' && !qv.hidden) close();
    });
  }

  /* ---------- intro loader: CSS runs the show; JS just records the session flag ---------- */
  function initIntroLoader() {
    var intro = document.getElementById('cbn-intro');
    if (!intro) return;
    if (document.documentElement.classList.contains('cbn-introed')) {
      intro.remove();
      return;
    }
    try { sessionStorage.setItem('cbn-introed', '1'); } catch (e) {}
    /* remove from the DOM once the CSS exit animation is done (0.7s delay + 0.3s fade) */
    setTimeout(function () { intro.remove(); }, 1200);
  }

  function boot() {
    initToggle();
    initMarquee();
    initStickyBar();
    initCartStick();
    initCardAtc();
    initJdgmEmptyGuard();
    initIntroReveal();
    initTiltIn(); /* claims cards (ours + Dawn grid items) before the reveal pass / animations.js */
    initPageEnrich(); /* adds .cbn-rv to page blocks before the reveal pass */
    initBadges();
    initPdpCounter();
    initReveals();
    initLenis();
    initDividers();
    initRails();
    initHero();
    initParallax();
    initSizeCompare();
    initQuickView();
    initIntroLoader();
    initFuse();
    initTierCelebrate();
    initEditPdp();
    initColIntro();
    initWhyVideo();
    initAutoRails();
    initPdpReviews();
    initRevLoaders();
    initShopSpots();
    if (hasGsap && !reduced) {
      window.addEventListener('load', function () {
        ScrollTrigger.refresh();
      }, { once: true });
    }
  }
})();
